import {
  call,
  put,
  takeEvery,
  takeLatest,
  select,
  all,
} from 'redux-saga/effects';
import { fragmentApi } from '../services';

import {
  LOADING_DATA_START,
  LOADING_DATA_FINISHED,
} from '../reducers/loading.reducer';

import {
  BOOKLET_SECTION_FRAGMENT_EDIT,
  BOOKLET_SECTION_FRAGMENT_DELETE,
  BOOKLET_SECTION_FRAGMENT_ADD,
  BOOKLET_SECTION_FRAGMENT_ADD_SUCCESS,
  BOOKLET_SECTION_SAVE_SUCCESS,
  BOOKLET_SECTION_CHILD_FRAGMENT_EDIT,
  BOOKLET_SECTION_CHILD_FRAGMENT_ADD,
  BOOKLET_SECTION_CHILD_FRAGMENT_ADD_SUCCESS,
  BOOKLET_SECTION_CHILD_FRAGMENT_ADD_FAILURE,
  BOOKLET_SECTION_CHILD_FRAGMENT_DELETE,
  BOOKLET_SECTION_CHILD_FRAGMENT_EDIT_SUCCESS,
  BOOKLET_SECTION_CHILD_FRAGMENT_EDIT_FAILURE,
  BOOKLET_GALLERY_IMAGE_REMOVE,
  BOOKLET_GALLERY_IMAGES_ADD,
} from '../reducers/booklet.reducer';
import { handleError } from './helper';
import { handleEditSectionRequest } from './section.saga';
import * as bookletActions from '../reducers/booklet.reducer';
import * as bookletSelectors from '../selectors/booklet.selectors';
import { showNotification } from '../helpers/notification';

function* saveFragmentRequest({
  payload: { sectionIndex, fragmentIndex, fragment },
}) {
  try {
    yield put({ type: LOADING_DATA_START });

    const bookletId = yield select(bookletSelectors.getBookletId);
    const sectionId = yield select(bookletSelectors.getSectionId(sectionIndex));
    const layouts = yield select(
      bookletSelectors.getSectionLayouts(sectionIndex)
    );

    yield call(fragmentApi.updateFragment, {
      bookletId,
      sectionId,
      id: fragment.id,
      body: { fragment, layouts },
    });
  } catch (error) {
    const message = yield* handleError(error, 'Fragment save failed.');
    showNotification(message);
  }
  yield put({ type: LOADING_DATA_FINISHED });
}

function* addFragmentRequest({
  payload: { sectionIndex, fragment, currentBreakpoint },
}) {
  try {
    yield put({ type: LOADING_DATA_START });

    const bookletId = yield select(bookletSelectors.getBookletId);
    let sectionId = yield select(bookletSelectors.getSectionId(sectionIndex));

    if (!sectionId) {
      //section not saved to API, call it
      const section = yield select(bookletSelectors.getSection(sectionIndex));

      try {
        const { data } = yield handleEditSectionRequest(
          bookletId,
          sectionIndex,
          section
        );
        yield put({
          type: BOOKLET_SECTION_SAVE_SUCCESS,
          payload: { index: sectionIndex, section: data },
        });

        sectionId = data.id;
      } catch (error) {
        const message = yield* handleError(error, 'Booklet save failed.');
        showNotification(message);
      }
    }

    const layouts = {};
    layouts[currentBreakpoint] = {
      w: fragment.props.w,
      h: fragment.props.h,
      x: fragment.props.x,
      y: fragment.props.y,
      minH: fragment.props.minH,
      minW: fragment.props.minW,
    };

    const {
      data: { fragment: newFragment, layouts: newLayouts },
    } = yield call(fragmentApi.createFragment, {
      bookletId,
      sectionId,
      body: { fragment, layouts },
    });

    yield put({
      type: BOOKLET_SECTION_FRAGMENT_ADD_SUCCESS,
      payload: {
        sectionIndex,
        fragment: { ...newFragment, extraConfig: fragment.extraConfig },
        layouts: newLayouts,
      },
    });
  } catch (error) {
    const message = yield* handleError(error, 'Adding new fragment failed.');
    showNotification(message);
  }
  yield put({ type: LOADING_DATA_FINISHED });
}

function* deleteFragmentRequest({ payload: { sectionIndex, fragmentId } }) {
  try {
    yield put({ type: LOADING_DATA_START });

    const bookletId = yield select(bookletSelectors.getBookletId);
    const sectionId = yield select(bookletSelectors.getSectionId(sectionIndex));

    if (fragmentId) {
      yield call(fragmentApi.deleteFragment, {
        bookletId,
        sectionId,
        fragmentId,
      });
    }
  } catch (error) {
    const message = yield* handleError(error, 'Delete fragment failed.');
    showNotification(message);
  }

  yield put({ type: LOADING_DATA_FINISHED });
}

function* addRowInGallery(imagesCount, sectionIndex, parentFragmentIndex) {
  const childrenLength = yield select(
    bookletSelectors.getSectionFragmentChildrenLength(
      sectionIndex,
      parentFragmentIndex
    )
  );
  const numberOfImagesInRow = 4;

  const rowsBeforeCount = Math.ceil(childrenLength / numberOfImagesInRow) || 1;
  const rowsAfterCount = Math.ceil(
    (childrenLength + imagesCount) / numberOfImagesInRow
  );
  const changeInRows = rowsAfterCount - rowsBeforeCount;

  if (changeInRows) {
    yield modifySectionHeight(
      parentFragmentIndex,
      sectionIndex,
      7 * changeInRows
    );
  }
}

function* removeRowInGallery(imagesCount, sectionIndex, parentFragmentIndex) {
  const childrenLength = yield select(
    bookletSelectors.getSectionFragmentChildrenLength(
      sectionIndex,
      parentFragmentIndex
    )
  );

  const rowRemoved = childrenLength > 0 && childrenLength % 4 === 0;
  if (rowRemoved) {
    yield modifySectionHeight(parentFragmentIndex, sectionIndex, -7);
  }
}

function* modifySectionHeight(parentFragmentIndex, sectionIndex, height) {
  const section = yield select(bookletSelectors.getSection(sectionIndex));

  const updatedSection = {
    ...section,
    layouts: {
      ...section.layouts,
      lg: [
        ...section.layouts.lg.filter((el, i) => i !== parentFragmentIndex),
        {
          ...section.layouts.lg[parentFragmentIndex],
          h: section.layouts.lg[parentFragmentIndex].h + height,
          minH: section.layouts.lg[parentFragmentIndex].minH + height,
        },
      ],
    },
  };

  yield put(
    bookletActions.editSectionLayout(sectionIndex, { ...updatedSection })
  );
}

function* deleteGalleryImageRequest({
  payload: { sectionIndex, parentFragmentIndex, fragmentIndex, fragmentId },
}) {
  yield put(
    bookletActions.deleteChildFragment(
      sectionIndex,
      parentFragmentIndex,
      fragmentIndex,
      fragmentId
    )
  );

  yield removeRowInGallery(-1, sectionIndex, parentFragmentIndex);
}

function* editChildFragmentRequest({
  payload: { sectionIndex, parentFragmentIndex, fragmentIndex, fragment },
}) {
  try {
    yield put({ type: LOADING_DATA_START });
    const bookletId = yield select(bookletSelectors.getBookletId);
    const sectionId = yield select(bookletSelectors.getSectionId(sectionIndex));

    const { data } = yield call(fragmentApi.updateFragment, {
      bookletId,
      sectionId,
      id: fragment.id,
      body: { fragment, layouts: {} },
    });

    yield put({
      type: BOOKLET_SECTION_CHILD_FRAGMENT_EDIT_SUCCESS,
      payload: {
        sectionIndex,
        parentFragmentIndex,
        fragmentIndex,
        fragment: data,
      },
    });
  } catch (error) {
    const message = yield* handleError(error);
    yield put({
      type: BOOKLET_SECTION_CHILD_FRAGMENT_EDIT_FAILURE,
      payload: message,
    });
  }
  yield put({ type: LOADING_DATA_FINISHED });
}

function* addGalleryImagesRequest({
  payload: { sectionIndex, parentFragmentIndex, images, id },
}) {
  try {
    yield put({ type: LOADING_DATA_START });
    const bookletId = yield select(bookletSelectors.getBookletId);
    let sectionId = yield select(bookletSelectors.getSectionId(sectionIndex));

    yield addRowInGallery(images.length, sectionIndex, parentFragmentIndex);

    const imageWithConfig = data => ({
      type: 'image',
      name: `image_${Date.now()}`,
      parentId: id,
      isLikeable: true,
      config: {
        canBeLikeable: true,
        ...data,
      },
    });

    const addedFragments = yield all(
      images.map(image =>
        call(fragmentApi.createFragment, {
          bookletId,
          sectionId,
          body: {
            fragment: imageWithConfig(image),
            layouts: {},
          },
        })
      )
    );

    yield all(
      addedFragments
        .map(el => el.data.fragment)
        .map(addedFragment =>
          put({
            type: BOOKLET_SECTION_CHILD_FRAGMENT_ADD_SUCCESS,
            payload: {
              sectionIndex,
              parentFragmentIndex,
              fragment: addedFragment,
            },
          })
        )
    );
  } catch (error) {
    const message = yield* handleError(error);
    showNotification(message);
  }

  yield put({ type: LOADING_DATA_FINISHED });
}

function* addChildFragmentRequest({
  payload: { sectionIndex, parentFragmentIndex, fragment },
}) {
  try {
    yield put({ type: LOADING_DATA_START });

    const bookletId = yield select(bookletSelectors.getBookletId);
    let sectionId = yield select(bookletSelectors.getSectionId(sectionIndex));

    const {
      data: { fragment: newFragment },
    } = yield call(fragmentApi.createFragment, {
      bookletId,
      sectionId,
      body: { fragment, layouts: {} },
    });

    yield put({
      type: BOOKLET_SECTION_CHILD_FRAGMENT_ADD_SUCCESS,
      payload: {
        sectionIndex,
        parentFragmentIndex,
        fragment: newFragment,
      },
    });
  } catch (error) {
    const message = yield* handleError(error);
    console.error(error, message);
    yield put({
      type: BOOKLET_SECTION_CHILD_FRAGMENT_ADD_FAILURE,
      payload: message,
    });
  }
  yield put({ type: LOADING_DATA_FINISHED });
}

export function* saveFragment() {
  yield takeLatest(BOOKLET_SECTION_FRAGMENT_EDIT, saveFragmentRequest);
}

export function* editChildFragment() {
  yield takeLatest(
    BOOKLET_SECTION_CHILD_FRAGMENT_EDIT,
    editChildFragmentRequest
  );
}

export function* addFragment() {
  yield takeLatest(BOOKLET_SECTION_FRAGMENT_ADD, addFragmentRequest);
}

export function* addChildFragment() {
  yield takeLatest(BOOKLET_SECTION_CHILD_FRAGMENT_ADD, addChildFragmentRequest);
}

export function* deleteFragment() {
  yield takeEvery(BOOKLET_SECTION_FRAGMENT_DELETE, deleteFragmentRequest);
}

export function* deleteChildFragment() {
  yield takeEvery(BOOKLET_SECTION_CHILD_FRAGMENT_DELETE, deleteFragmentRequest);
}

export function* deleteGalleryImage() {
  yield takeEvery(BOOKLET_GALLERY_IMAGE_REMOVE, deleteGalleryImageRequest);
}

export function* addGalleryImages() {
  yield takeEvery(BOOKLET_GALLERY_IMAGES_ADD, addGalleryImagesRequest);
}



// WEBPACK FOOTER //
// ./src/sagas/fragment.saga.js